import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FileUpload from 'core-system/FileUpload/FileUpload'
import FlexContainer from 'core-system/FlexContainer'
import SearchableDropdown from 'core-system/Dropdown/SearchableDropdown'
import { SelectItemProps } from 'core-system/Dropdown'
import palette from 'core-system/Themes/palette'
import { Transit, Parking } from 'core-system/Icons/Flex'
import { Rideshare, Car } from 'core-system/Icons/modeTypes'

const Subtitle = styled(Text)`
  color: ${palette.text.secondary};
  margin-bottom: 1.5rem;
`

const EmployeeSelectorSection = styled.div`
  margin-bottom: 1.5rem;
`

const EmployeeSelectorLabel = styled(Text)`
  font-weight: 500;
  margin-bottom: 0.5rem;
`

const InfoPanel = styled.div`
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background-color: ${palette.grey.grey50};
  border-left: 3px solid ${palette.secondary.purple300};
  border-radius: 0.25rem;
`

const InfoLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 0.625rem;
`

const ReceiptTypesContainer = styled(FlexContainer)`
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const Chip = styled(FlexContainer)`
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background-color: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 1rem;
`

const ChipText = styled(Text)`
  color: ${palette.text.primary};
  font-size: 0.8125rem;
  font-weight: 500;
`

const HintText = styled.div`
  color: ${palette.text.placeholder};
  font-size: 0.75rem;
  font-style: italic;
  line-height: 1.5;

  a {
    color: ${palette.secondary.purple600};
    text-decoration: underline;
    cursor: pointer;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${palette.grey.grey200};
  margin: 0.75rem 0;
`

const UploadErrorText = styled(Text)`
  color: ${palette.error.red500};
  margin-top: 0.5rem;
`

const receiptTypes = [
  {
    label: 'Transit',
    icon: <Transit width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Parking',
    icon: <Parking width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Tolls',
    icon: <Car width={16} height={16} color={palette.text.secondary} />,
  },
  {
    label: 'Rideshare',
    icon: <Rideshare width={16} height={16} color={palette.text.secondary} />,
  },
]

interface SubmitReimbursementCardProps {
  onUpload: (files: File[], employeeId?: string) => Promise<void>
  employeeOptions?: SelectItemProps[]
  showEmployeeSelector?: boolean
  onEmployeeSearch?: (value: string) => void
}

const SubmitReimbursementCard = React.memo(
  ({
    onUpload,
    employeeOptions,
    showEmployeeSelector = false,
    onEmployeeSearch,
  }: SubmitReimbursementCardProps) => {
    const [file, setFile] = useState([])
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
      null
    )

    const handleEmployeeSelect = useCallback((item: SelectItemProps) => {
      setSelectedEmployeeId(item ? item.id : null)
      setUploadError(null)
    }, [])

    const handleUpload = useCallback(
      async (files: File[]) => {
        setUploadError(null)
        await onUpload(files, selectedEmployeeId || undefined)
        setFile([])
      },
      [onUpload, selectedEmployeeId]
    )

    const handleUploadError = useCallback((error: string) => {
      setUploadError(error)
    }, [])

    const isUploadDisabled = showEmployeeSelector && !selectedEmployeeId

    return (
      <>
        <Subtitle variant='body1'>
          Upload receipts for{' '}
          <strong>eligible expenses covered under your Fleet programs</strong>.
          Eligibility varies by program. Some programs may only cover specific
          expense types.
        </Subtitle>

        {showEmployeeSelector && employeeOptions && (
          <EmployeeSelectorSection>
            <EmployeeSelectorLabel variant='body2'>
              Select Employee
            </EmployeeSelectorLabel>
            <SearchableDropdown
              options={employeeOptions}
              itemCallback={handleEmployeeSelect}
              onSearchChange={onEmployeeSearch}
            />
          </EmployeeSelectorSection>
        )}

        <FileUpload
          key={selectedEmployeeId || 'no-employee'}
          placeholder={
            isUploadDisabled
              ? 'Select an employee first'
              : 'Click to upload or drag and drop'
          }
          acceptedFilesTypes={['.png', '.jpeg', '.jpg', '.heic', '.pdf']}
          placeholderSubtitle='PDF, JPG, PNG, or HEIC (max. 10MB)'
          onChange={setFile}
          files={file}
          onError={handleUploadError}
          uploadFiles={handleUpload}
          disabled={isUploadDisabled}
        />
        {uploadError && (
          <UploadErrorText variant='body2'>{uploadError}</UploadErrorText>
        )}
        <InfoPanel>
          <InfoLabel variant='body2'>
            Examples of eligible expenses (depending on the program):
          </InfoLabel>
          <ReceiptTypesContainer>
            {receiptTypes.map((type) => (
              <Chip key={type.label}>
                {type.icon}
                <ChipText variant='body2'>{type.label}</ChipText>
              </Chip>
            ))}
          </ReceiptTypesContainer>
          <Divider />
          <HintText>
            Receipts for expense types not covered by the applicable program may
            be denied.
          </HintText>
          <HintText>
            Not sure what's covered? Check program details or contact{' '}
            <a href='mailto:support@movewithfleet.com'>support</a>.
          </HintText>
        </InfoPanel>
      </>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  SubmitReimbursementCard.displayName = 'SubmitReimbursementCard'
}

export default SubmitReimbursementCard
